// Offline API service - All data is embedded, no backend required
import { STORES_DATA, COUPONS_DATA } from './staticData';

// Simulate API delay for realistic feel (optional, can be 0)
const SIMULATED_DELAY = 100;

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Stores API
export const getStores = async (featured = null) => {
  await delay(SIMULATED_DELAY);
  try {
    let stores = [...STORES_DATA];
    if (featured !== null) {
      stores = stores.filter(store => store.featured === featured);
    }
    return stores;
  } catch (error) {
    console.error('Error fetching stores:', error);
    return [];
  }
};

export const getStore = async (storeId) => {
  await delay(SIMULATED_DELAY);
  try {
    const store = STORES_DATA.find(s => s.id === storeId);
    return store || null;
  } catch (error) {
    console.error('Error fetching store:', error);
    return null;
  }
};

// Coupons API
export const getCoupons = async (filters = {}) => {
  await delay(SIMULATED_DELAY);
  try {
    let coupons = [...COUPONS_DATA];
    
    if (filters.store_id) {
      coupons = coupons.filter(c => c.store_id === filters.store_id);
    }
    if (filters.category) {
      coupons = coupons.filter(c => c.category === filters.category);
    }
    if (filters.featured !== undefined) {
      coupons = coupons.filter(c => c.featured === filters.featured);
    }
    if (filters.verified !== undefined) {
      coupons = coupons.filter(c => c.verified === filters.verified);
    }
    
    return coupons;
  } catch (error) {
    console.error('Error fetching coupons:', error);
    return [];
  }
};

export const getTrendingCoupons = async (limit = 12) => {
  await delay(SIMULATED_DELAY);
  try {
    // Get unique coupons by store (one per store)
    const seenStores = new Set();
    const trending = [];
    
    for (const coupon of COUPONS_DATA) {
      if (!seenStores.has(coupon.store_id)) {
        seenStores.add(coupon.store_id);
        trending.push(coupon);
        if (trending.length >= limit) break;
      }
    }
    
    return trending;
  } catch (error) {
    console.error('Error fetching trending coupons:', error);
    return [];
  }
};

export const getFeaturedCoupons = async (limit = 6) => {
  await delay(SIMULATED_DELAY);
  try {
    const featured = COUPONS_DATA.filter(c => c.featured).slice(0, limit);
    return featured;
  } catch (error) {
    console.error('Error fetching featured coupons:', error);
    return [];
  }
};

// Categories API
export const getCategories = async () => {
  await delay(SIMULATED_DELAY);
  try {
    // Extract unique categories from coupons
    const categorySet = new Set();
    COUPONS_DATA.forEach(c => {
      if (c.category) categorySet.add(c.category);
    });
    
    const categories = Array.from(categorySet).map(name => ({
      id: name.toLowerCase().replace(/\s+/g, '-'),
      name,
      icon: 'Tag',
      count: COUPONS_DATA.filter(c => c.category === name).length
    }));
    
    return categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

// Search API
export const searchCoupons = async (query) => {
  await delay(SIMULATED_DELAY);
  try {
    const q = query.toLowerCase();
    
    // Search stores
    const stores = STORES_DATA.filter(store => 
      store.name.toLowerCase().includes(q) ||
      store.domain.toLowerCase().includes(q) ||
      (store.description && store.description.toLowerCase().includes(q))
    );
    
    // Search coupons
    const coupons = COUPONS_DATA.filter(coupon =>
      coupon.title.toLowerCase().includes(q) ||
      (coupon.description && coupon.description.toLowerCase().includes(q)) ||
      (coupon.category && coupon.category.toLowerCase().includes(q)) ||
      (coupon.store_name && coupon.store_name.toLowerCase().includes(q))
    );
    
    return { stores, coupons };
  } catch (error) {
    console.error('Error searching:', error);
    return { stores: [], coupons: [] };
  }
};

// Search suggestions for autocomplete
export const getSearchSuggestions = async (query) => {
  await delay(50); // Faster for autocomplete
  try {
    const q = query.toLowerCase();
    
    const suggestions = STORES_DATA
      .filter(store => 
        store.name.toLowerCase().startsWith(q) ||
        store.domain.toLowerCase().startsWith(q)
      )
      .slice(0, 5)
      .map(store => ({
        id: store.id,
        name: store.name,
        logo: store.logo
      }));
    
    return suggestions;
  } catch (error) {
    console.error('Error fetching suggestions:', error);
    return [];
  }
};
